/**
 * Encode a Uint8Array to a base64 string without using the spread operator.
 *
 * The common pattern `btoa(String.fromCharCode(...bytes))` spreads the entire
 * array as function arguments, which overflows the call stack on V8 for inputs
 * larger than ~65 KB. This loop-based approach has no size limit.
 */
export function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** Decode a base64 string to a Uint8Array (inverse of uint8ToBase64). */
export function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Write a large string to a PTY session in chunks to avoid overwhelming
 * the terminal's input buffer. Without chunking, writes >4KB can cause
 * the shell to drop or truncate the beginning of the text.
 *
 * Each chunk is written as a separate base64-encoded PTY write with a
 * small delay between chunks to let the terminal process them.
 */
export async function writeTextToPty(
  text: string,
  sessionId: number,
  writeFn: (sessionId: number, data: string) => Promise<void>,
  bracketedPaste = false,
): Promise<void> {
  const CHUNK_SIZE = 1024; // characters per write — safe for all shells
  const CHUNK_DELAY_MS = 30; // breathing room between chunks
  const encoder = new TextEncoder();

  // Bracketed paste mode (DECSET 2004): wrap the text in ESC[200~ … ESC[201~ so
  // a paste-aware app (shells, CLI agents) treats embedded newlines as literal
  // pasted content instead of executing each line. Used for multi-line enhanced
  // transcriptions so bullets/paragraphs don't fire as commands.
  if (bracketedPaste) {
    text = `\x1b[200~${text}\x1b[201~`;
  }

  for (let offset = 0; offset < text.length; offset += CHUNK_SIZE) {
    const chunk = text.slice(offset, offset + CHUNK_SIZE);
    const bytes = encoder.encode(chunk);
    const encoded = uint8ToBase64(bytes);
    await writeFn(sessionId, encoded);
    // Small delay between chunks so the terminal can process each one
    if (offset + CHUNK_SIZE < text.length) {
      await new Promise((r) => setTimeout(r, CHUNK_DELAY_MS));
    }
  }
}
